/**
 * ScrollController
 * スクロール連動のアニメーションを管理する
 * IntersectionObserver でフェードイン等を実装
 */
export const ScrollController = {
  init() {
    this.splitChars();
    this.observeFadeIn();
    this.observeVisible();
  },

  /**
   * 対象要素のテキストを 1文字ずつ `<span class="char" style="--char-index: n">`
   * に分割する。CSS 側で transition-delay: calc(var(--char-index) * ...) を効かせ、
   * 親 (もしくは自身) に `is-visible` が付いた時に左から順にフェードインさせる。
   *
   * 対象:
   *   - `.js-split-chars` … 汎用トリガー (任意の要素に付与)
   *   - `.headline__title` / `.headline__index span` … 既存 headline は自動対象
   */
  splitChars() {
    const targets = document.querySelectorAll(
      '.js-split-chars, .headline__title, .headline__index span'
    );
    targets.forEach( ( el ) => {
      // Barba SPA 遷移時の再初期化で二重分割しないためのガード
      if ( el.dataset.splitChars === 'done' ) return;
      if ( !el.textContent.trim() ) return;

      let charIndex = 0;

      // 再帰的にノードを走査し、テキストは 1文字ずつ .char に分割、
      // 要素 (例: <br class="tw-hidden sm:tw-block">) はそのまま複製する。
      // これにより「改行を保ったまま」の 1文字フェードインが可能になる。
      const walk = ( node, parent ) => {
        if ( node.nodeType === Node.TEXT_NODE ) {
          // 連続空白 (ソースのインデント/改行含む) を 1個に圧縮
          let text = node.textContent.replace( /\s+/g, ' ' );
          // 前が無い or 前が要素 (br 等) の場合は先頭空白を除去
          const prev = node.previousSibling;
          if ( !prev || prev.nodeType === Node.ELEMENT_NODE ) {
            text = text.replace( /^ /, '' );
          }
          // 次が無い (親の最後) の場合のみ末尾空白を除去。
          // 次が要素 (br 等) の場合は保持 — CSS 側で br が非表示のときに
          // 前後テキストが「詞と詞をくっつけて表示」されないようにするため。
          if ( !node.nextSibling ) {
            text = text.replace( / $/, '' );
          }
          if ( !text ) return;
          [ ...text ].forEach( ( ch ) => {
            const span = document.createElement( 'span' );
            span.className = 'char';
            span.style.setProperty( '--char-index', charIndex++ );
            // 半角スペースは inline-block では潰れるので nbsp に置換
            span.innerHTML = ch === ' ' ? '&nbsp;' : ch;
            parent.appendChild( span );
          } );
        } else if ( node.nodeType === Node.ELEMENT_NODE ) {
          // 要素 (br 等) は属性込みで複製し、その子ノードを再帰処理
          const clone = node.cloneNode( false );
          parent.appendChild( clone );
          node.childNodes.forEach( ( child ) => walk( child, clone ) );
        }
      };

      const frag = document.createDocumentFragment();
      el.childNodes.forEach( ( child ) => walk( child, frag ) );
      el.textContent = '';
      el.appendChild( frag );
      el.dataset.splitChars = 'done';
    } );
  },

  observeFadeIn() {
    const targets = document.querySelectorAll( '.js-fade-in' );
    if ( !targets.length ) return;

    const observer = new IntersectionObserver( ( entries ) => {
      entries.forEach( ( entry ) => {
        if ( entry.isIntersecting ) {
          entry.target.classList.add( 'is-visible' );
          observer.unobserve( entry.target );
        }
      } );
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    } );

    targets.forEach( ( el ) => observer.observe( el ) );
  },

  observeVisible() {
    const targets = document.querySelectorAll( '.js-visible' );
    if ( !targets.length ) return;

    const observer = new IntersectionObserver( ( entries ) => {
      entries.forEach( ( entry ) => {
        if ( entry.isIntersecting ) {
          entry.target.classList.add( 'is-visible' );
          observer.unobserve( entry.target );
        }
      } );
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    } );

    targets.forEach( ( el ) => observer.observe( el ) );
  },
};
